import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { famousInvestors } from '../shared/data/famousInvestors';
import { useDashboardData } from '../shared/providers/dashboardContext';
import type { PersonaData } from '../shared/services/personaService';

const traitKeys = [
  'macroVsMicro',
  'valueVsGrowth',
  'timeHorizon',
  'passiveVsActive',
  'strategicVsFinancial',
] as const;

type TraitKey = (typeof traitKeys)[number];

const segmentColors: Record<string, { fill: string; accent: string; shadow: string }> = {
  GDI: { fill: 'rgba(41, 27, 76, 0.4)', accent: 'rgba(41, 27, 76, 0.95)', shadow: 'rgba(41, 27, 76, 0.42)' },
  TPC: { fill: 'rgba(139, 92, 60, 0.4)', accent: 'rgba(139, 92, 60, 0.95)', shadow: 'rgba(139, 92, 60, 0.42)' },
  PFA: { fill: 'rgba(32, 91, 79, 0.4)', accent: 'rgba(32, 91, 79, 0.95)', shadow: 'rgba(32, 91, 79, 0.42)' },
};

const defaultMarkerFill = 'rgba(41, 27, 76, 0.28)';
const defaultMarkerAccent = 'rgba(41, 27, 76, 0.8)';
const defaultMarkerShadow = 'rgba(41, 27, 76, 0.3)';

type PersonaPoint = {
  id: string;
  teamName: string;
  segment: string | null;
  matchPercentage: number;
  valueVsGrowth: number;
  macroVsMicro: number;
  timeHorizon: number;
  passiveVsActive: number;
  strategicVsFinancial: number;
};

type PersonaTooltipData = Pick<PersonaPoint, 'teamName'>;

const clampTrait = (value: unknown): number => {
  const numericValue =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : NaN;
  if (!Number.isFinite(numericValue)) {
    return 0;
  }
  if (numericValue >= 0 && numericValue <= 1) {
    return Math.round(numericValue * 100);
  }
  return Math.max(0, Math.min(100, Math.round(numericValue)));
};


const getPersonaTraitValue = (persona: PersonaData, key: TraitKey): number => {
  const rawValue = (persona as unknown as Record<string, unknown>)[key];
  return clampTrait(rawValue);
};

const clampJitter = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const getDeterministicShift = (id: string, axis: string): number => {
  const key = `${id}-${axis}`;
  const shifts = [-1, 0, 1];

  const shiftIndex = Array.from(key).reduce(
    (accumulator, character) => (accumulator + character.charCodeAt(0)) % shifts.length,
    0
  );

  return shifts[shiftIndex];
};

function personaIsProcessed(persona: PersonaData): boolean {
  return persona.status !== 'processing';
}

const resolvePersonaColor = (segment: string | null, highlight: boolean) => {
  if (highlight && segment) {
    const colors = segmentColors[segment];
    if (colors) {
      return {
        fill: colors.fill,
        accent: colors.accent,
        shadow: colors.shadow,
        isColored: true,
      };
    }
  }
  return {
    fill: defaultMarkerFill,
    accent: defaultMarkerAccent,
    shadow: defaultMarkerShadow,
    isColored: false,
  };
};

function PersonaMarker({
  persona,
  fill,
  accent,
  shadow,
  isColored,
  placement = 'top',
}: {
  persona: PersonaTooltipData;
  fill: string;
  accent: string;
  shadow: string;
  isColored: boolean;
  placement?: 'top' | 'bottom';
}) {
  const positionClass =
    placement === 'bottom'
      ? 'top-full left-1/2 -translate-x-1/2 mt-2'
      : '-top-20 left-1/2 -translate-x-1/2';

  return (
    <div className="group relative flex h-full w-full items-center justify-center">
      <div
        className="h-full w-full rounded-full border shadow-sm transition-transform group-hover:scale-110 cursor-pointer"
        style={{
          backgroundColor: fill,
          borderColor: accent,
          borderWidth: isColored ? 2 : 1,
          boxShadow: `0 4px 12px ${shadow}`,
        }}
      />
      <div
        className={`pointer-events-none absolute ${positionClass} bg-[#291B4C] text-white px-3 py-2 rounded-lg text-xs shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-normal max-w-[240px]`}
      >
        <div className="font-semibold">{persona.teamName}</div>
      </div>
    </div>
  );
}

function SegmentLegend() {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-[#291B4C]/10 bg-white px-6 py-4 text-xs font-semibold text-[#291B4C]/70 shadow-sm">
      {(['GDI', 'TPC', 'PFA'] as const).map((segment) => (
        <div
          key={segment}
          className="flex items-center gap-2 px-3 py-1 rounded-full border border-[#291B4C]/15 bg-[#291B4C]/5"
        >
          <span
            className="h-2.5 w-2.5 rounded-full border"
            style={{
              backgroundColor: segmentColors[segment].fill,
              borderColor: segmentColors[segment].accent,
            }}
          />
          <span className="uppercase tracking-wider">{segment}</span>
        </div>
      ))}
    </div>
  );
}

export function DashboardPositioningPage() {
  const pointDiameter = 32;
  const timeHorizonVerticalSpacing = 36;
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);

  const {
    personaStats,
    personaStatsLoading,
    personaStatsError,
    personas,
    personasLoading,
    personaError,
    showInvestors,
    setShowInvestors,
    showSegmentColors,
    setShowSegmentColors,
  } = useDashboardData();

  const loading = personaStatsLoading || personasLoading;

  const firmProfile = useMemo(() => {
    if (!personaStats) {
      return null;
    }

    return traitKeys.reduce(
      (accumulator, key) => {
        accumulator[key] = clampTrait(personaStats.aggregateTraits[key]);
        return accumulator;
      },
      {} as Record<TraitKey, number>
    );
  }, [personaStats]);

  const personaSimilarities = useMemo<PersonaPoint[]>(() => {
    if (!firmProfile) {
      return [];
    }

    return personas
      .filter(personaIsProcessed) // Remove personas with all 0 values
      .map((persona) => {
        const diffSum = traitKeys.reduce((acc, key) => {
          const personaValue = getPersonaTraitValue(persona, key);
          const diff = Math.abs(firmProfile[key] - personaValue);
          return acc + diff;
        }, 0);

        const valueVsGrowth = getPersonaTraitValue(persona, 'valueVsGrowth');
        const macroVsMicro = getPersonaTraitValue(persona, 'macroVsMicro');

        const teamName =
          (persona as unknown as Record<string, unknown>).teamName ?? 'Unknown Team';
        const rawSegment = (persona as unknown as Record<string, unknown>).segment;
        const segment =
          typeof rawSegment === 'string' && rawSegment.trim().length > 0
            ? rawSegment.trim()
            : null;

        return {
          id: persona.id,
          teamName:
            typeof teamName === 'string' && teamName.trim().length > 0
              ? teamName
              : 'Unknown Team',
          segment,
          matchPercentage: Math.max(0, 100 - diffSum / traitKeys.length),
          valueVsGrowth,
          macroVsMicro,
          timeHorizon: getPersonaTraitValue(persona, 'timeHorizon'),
          passiveVsActive: getPersonaTraitValue(persona, 'passiveVsActive'),
          strategicVsFinancial: getPersonaTraitValue(persona, 'strategicVsFinancial'),
        };
      })
      .filter(
        (persona) =>
          Number.isFinite(persona.valueVsGrowth) && Number.isFinite(persona.macroVsMicro)
      );
  }, [firmProfile, personas]);

  const jitteredPersonas = useMemo(
    () =>
      personaSimilarities.map((persona) => {
        const xShift = getDeterministicShift(persona.id, 'primary-x');
        const yShift = getDeterministicShift(persona.id, 'primary-y');

        return {
          ...persona,
          jitteredValueVsGrowth: clampJitter(persona.valueVsGrowth + xShift, 0, 100),
          jitteredMacroVsMicro: clampJitter(persona.macroVsMicro + yShift, 0, 100),
        };
      }),
    [personaSimilarities]
  );

  const jitteredEngagementPersonas = useMemo(
    () =>
      personaSimilarities
        .filter(
          (persona) =>
            Number.isFinite(persona.passiveVsActive) &&
            Number.isFinite(persona.strategicVsFinancial)
        )
        .map((persona) => {
          const xShift = getDeterministicShift(persona.id, 'secondary-x');
          const yShift = getDeterministicShift(persona.id, 'secondary-y');

          return {
            ...persona,
            jitteredStrategicVsFinancial: clampJitter(
              persona.strategicVsFinancial + xShift,
              0,
              100
            ),
            jitteredPassiveVsActive: clampJitter(
              persona.passiveVsActive + yShift,
              0,
              100
            ),
          };
        }),
    [personaSimilarities]
  );

  const jitteredTimeHorizonPoints = useMemo(
    () =>
      personaSimilarities
        .filter((persona) => Number.isFinite(persona.timeHorizon))
        .map((persona) => {
          const horizontalShift = getDeterministicShift(
            persona.id,
            'timeline-x'
          );
          const verticalShift = getDeterministicShift(
            persona.id,
            'timeline-y'
          );

          return {
            ...persona,
            jitteredTimeHorizon: clampJitter(persona.timeHorizon + horizontalShift, 0, 100),
            jitteredTimeHorizonOffset: verticalShift * timeHorizonVerticalSpacing,
          };
        }),
    [personaSimilarities, timeHorizonVerticalSpacing]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#291B4C] animate-spin mx-auto mb-4" strokeWidth={2} />
          <p className="text-lg font-semibold text-[#291B4C]">Loading personas...</p>
        </div>
      </div>
    );
  }

  if (!firmProfile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md space-y-3">
          <h2 className="text-2xl font-bold text-[#291B4C] uppercase tracking-tight">
            Positioning unavailable
          </h2>
          <p className="text-sm text-[#291B4C]/70 leading-relaxed">
            {personaStatsError ??
              'We need processed workshop submissions to chart the TA25 profile. Once personas are uploaded, this page will map TA25\'s position relative to legendary investors and every processed team.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto px-12 py-12 space-y-10">
        {(personaError || personaStatsError) && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            {personaError ?? personaStatsError}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSegmentColors((prev) => !prev)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#291B4C]/20 text-xs font-semibold uppercase tracking-wider text-[#291B4C] hover:bg-[#291B4C]/5 transition-colors"
            >
              {showSegmentColors ? 'Hide Segments' : 'Show Segments'}
            </button>
            <button
              type="button"
              onClick={() => setShowInvestors((prev) => !prev)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#291B4C]/20 text-xs font-semibold uppercase tracking-wider text-[#291B4C] hover:bg-[#291B4C]/5 transition-colors"
            >
              {showInvestors ? 'Hide Famous Investors' : 'Show Famous Investors'}
            </button>
          </div>
        </div>

        <div className="border border-[#291B4C]/10 rounded-2xl p-10 bg-white space-y-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-[#291B4C] uppercase tracking-tight">
                Orientation and Value
              </h2>
              <p className="text-sm text-[#291B4C]/60">
                Macro vs. Micro orientation on the vertical axis, Value vs. Growth bias on the horizontal axis
              </p>
            </div>
            {showSegmentColors && (
              <div className="flex w-full justify-start sm:w-auto sm:justify-end">
                <SegmentLegend />
              </div>
            )}
          </div>

          <div className="relative h-[520px] bg-[#291B4C]/5 rounded-xl p-8">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              ↑ Macro
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              ↓ Micro
            </div>
            <div className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              ← Value
            </div>
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              Growth →
            </div>

            <div className="relative w-full h-full">
              {jitteredPersonas.map((persona) => {
                const placement: 'top' | 'bottom' =
                  persona.jitteredMacroVsMicro > 80 ? 'bottom' : 'top';
                const { fill, accent, shadow, isColored } = resolvePersonaColor(
                  persona.segment,
                  showSegmentColors
                );
                const markerId = `primary-${persona.id}`;
                return (
                  <div
                    key={persona.id}
                    className="absolute"
                    style={{
                      left: `${persona.jitteredValueVsGrowth}%`,
                      top: `${100 - persona.jitteredMacroVsMicro}%`,
                      width: pointDiameter,
                      height: pointDiameter,
                      transform: 'translate(-50%, -50%)',
                      zIndex: hoveredMarkerId === markerId ? 30 : 1,
                    }}
                    onMouseEnter={() => setHoveredMarkerId(markerId)}
                    onMouseLeave={() => setHoveredMarkerId((current) =>
                      current === markerId ? null : current
                    )}
                  >
                    <PersonaMarker
                      persona={{ teamName: persona.teamName }}
                      placement={placement}
                      fill={fill}
                      accent={accent}
                      shadow={shadow}
                      isColored={isColored}
                    />
                  </div>
                );
              })}

              {showInvestors &&
                famousInvestors.map((investor) => (
                  <div
                    key={investor.name}
                    className="absolute"
                    style={{
                      left: `${investor.traits.valueVsGrowth}%`,
                      top: `${100 - investor.traits.macroVsMicro}%`,
                      width: pointDiameter,
                      height: pointDiameter,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                    }}
                  >
                    <div className="w-full h-full rounded-full bg-white border-2 border-[#291B4C] shadow-md shadow-[#291B4C]/20 flex items-center justify-center relative group cursor-pointer">
                      <span className="text-[10px] font-bold text-[#291B4C]">
                        {investor.name.split(' ')[0][0]}
                        {investor.name.split(' ')[1]?.[0]}
                      </span>
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#291B4C] text-white px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        {investor.name}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="border border-[#291B4C]/10 rounded-2xl p-10 bg-white space-y-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-[#291B4C] uppercase tracking-tight">
                Engagement and Approach
              </h2>
              <p className="text-sm text-[#291B4C]/60">
                Passive vs. Active orientation on the vertical axis, Strategic vs. Financial approach on the horizontal axis
              </p>
            </div>
            {showSegmentColors && (
              <div className="flex w-full justify-start sm:w-auto sm:justify-end">
                <SegmentLegend />
              </div>
            )}
          </div>

          <div className="relative h-[520px] bg-[#291B4C]/5 rounded-xl p-8">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              ↑ Active
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              ↓ Passive
            </div>
            <div className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              ← Financial
            </div>
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              Strategic →
            </div>

            <div className="relative w-full h-full">
              {jitteredEngagementPersonas.map((persona) => {
                const placement: 'top' | 'bottom' =
                  persona.jitteredPassiveVsActive > 80 ? 'bottom' : 'top';
                const { fill, accent, shadow, isColored } = resolvePersonaColor(
                  persona.segment,
                  showSegmentColors
                );
                const markerId = `secondary-${persona.id}`;
                return (
                  <div
                    key={`${persona.id}-secondary`}
                    className="absolute"
                    style={{
                      left: `${persona.jitteredStrategicVsFinancial}%`,
                      top: `${100 - persona.jitteredPassiveVsActive}%`,
                      width: pointDiameter,
                      height: pointDiameter,
                      transform: 'translate(-50%, -50%)',
                      zIndex: hoveredMarkerId === markerId ? 30 : 1,
                    }}
                    onMouseEnter={() => setHoveredMarkerId(markerId)}
                    onMouseLeave={() => setHoveredMarkerId((current) =>
                      current === markerId ? null : current
                    )}
                  >
                    <PersonaMarker
                      persona={{ teamName: persona.teamName }}
                      placement={placement}
                      fill={fill}
                      accent={accent}
                      shadow={shadow}
                      isColored={isColored}
                    />
                  </div>
                );
              })}

              {showInvestors &&
                famousInvestors.map((investor) => (
                  <div
                    key={`${investor.name}-secondary`}
                    className="absolute"
                    style={{
                      left: `${investor.traits.strategicVsFinancial}%`,
                      top: `${100 - investor.traits.passiveVsActive}%`,
                      width: pointDiameter,
                      height: pointDiameter,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                    }}
                  >
                    <div className="w-full h-full rounded-full bg-white border-2 border-[#291B4C] shadow-md shadow-[#291B4C]/20 flex items-center justify-center relative group cursor-pointer">
                      <span className="text-[10px] font-bold text-[#291B4C]">
                        {investor.name.split(' ')[0][0]}
                        {investor.name.split(' ')[1]?.[0]}
                      </span>
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#291B4C] text-white px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        {investor.name}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="border border-[#291B4C]/10 rounded-2xl p-10 bg-white space-y-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-[#291B4C] uppercase tracking-tight">
                Time Horizon Profile
              </h2>
              <p className="text-sm text-[#291B4C]/60">
                Explore how personas balance near-term execution with long-term conviction along a single axis
              </p>
            </div>
            {showSegmentColors && (
              <div className="flex w-full justify-start sm:w-auto sm:justify-end">
                <SegmentLegend />
              </div>
            )}
          </div>

          <div className="relative h-[520px] bg-[#291B4C]/5 rounded-xl p-8">
            <div className="absolute inset-x-8 top-1/2 h-px bg-[#291B4C]/20" />
            <div className="absolute left-8 -translate-y-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              Short-Term
            </div>
            <div className="absolute right-8 -translate-y-1/2 text-xs font-bold text-[#291B4C]/60 uppercase tracking-wider">
              Long-Term
            </div>

            <div className="relative w-full h-full">
              {jitteredTimeHorizonPoints.map((persona) => {
                const leftPosition = 10 + persona.jitteredTimeHorizon * 0.8;
                const placement: 'top' | 'bottom' =
                  (persona.jitteredTimeHorizonOffset ?? 0) < 0 ? 'bottom' : 'top';
                const { fill, accent, shadow, isColored } = resolvePersonaColor(
                  persona.segment,
                  showSegmentColors
                );
                const markerId = `timeline-${persona.id}`;
                return (
                  <div
                    key={`${persona.id}-time-horizon`}
                    className="absolute"
                    style={{
                      left: `${leftPosition}%`,
                      top: `calc(50% + ${persona.jitteredTimeHorizonOffset}px)`,
                      width: pointDiameter,
                      height: pointDiameter,
                      transform: 'translate(-50%, -50%)',
                      zIndex: hoveredMarkerId === markerId ? 30 : 1,
                    }}
                    onMouseEnter={() => setHoveredMarkerId(markerId)}
                    onMouseLeave={() => setHoveredMarkerId((current) =>
                      current === markerId ? null : current
                    )}
                  >
                    <PersonaMarker
                      persona={{ teamName: persona.teamName }}
                      placement={placement}
                      fill={fill}
                      accent={accent}
                      shadow={shadow}
                      isColored={isColored}
                    />
                  </div>
                );
              })}

              {showInvestors &&
                famousInvestors.map((investor) => {
                  const offset = getDeterministicShift(
                    investor.name,
                    'timeline-investor'
                  );
                  const leftPosition = 10 + investor.traits.timeHorizon * 0.8;

                  return (
                    <div
                      key={`${investor.name}-time-horizon`}
                      className="absolute"
                      style={{
                        left: `${leftPosition}%`,
                        top: `calc(50% + ${offset * 8}px)`,
                        width: pointDiameter,
                        height: pointDiameter,
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                      }}
                    >
                      <div className="w-full h-full rounded-full bg-white border-2 border-[#291B4C] shadow-md shadow-[#291B4C]/20 flex items-center justify-center relative group cursor-pointer">
                        <span className="text-[10px] font-bold text-[#291B4C]">
                          {investor.name.split(' ')[0][0]}
                          {investor.name.split(' ')[1]?.[0]}
                        </span>
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#291B4C] text-white px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          {investor.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}